import React from "react";
import { makeStyles } from "@material-ui/core/styles";
import { MessageCard } from "../MessageCard";
import { DateDisplay } from "../DateDisplay";
import { OperationalConfigurationOverride } from "../../models/OperationalConfiguration";
import { ValueDisplayProps } from "../ValueDisplay";
import { List, ListItem, Fab } from "@material-ui/core";
import DeleteIcon from "@material-ui/icons/Delete";
import EditIcon from "@material-ui/icons/Edit";
import { GridValueDisplayList } from "../GridValueDisplayList";
import { PowFeature } from "../PowFeature";
import { POW_FEATURES } from "../../services/powFeatures.service";
import { deleteOperationalConfigurationOverride } from "../../services/api/operationalConfiguration.api";
import { useSnackbar } from "notistack";
import { showSnack } from "../../utils/component";

interface OperationalConfigurationOverridesListProps {
	// overrides list
	operationalConfigurationOverrides: OperationalConfigurationOverride[];
	// product id
	productId: string;
	// function to call on successful action
	onSuccess: () => void;
	// function to select an override to edit
	handleEdit: (operationalConfigurationOverride: OperationalConfigurationOverride) => void;
}

const useStyles = makeStyles((theme) => ({
	title: {
		marginBottom: theme.spacing(3),
	},
	list: {
		maxWidth: theme.breakpoints.values.md,
	},
	listItem: {
		boxShadow: theme.shadows[1],
		marginBottom: theme.spacing(2),
		padding: 16,
	},
	listSecondary: {
		marginTop: theme.spacing(0.5),
		fontSize: theme.typography.caption.fontSize,
	},
	actionButtons: {
		position: "absolute",
		bottom: 12,
		right: 12,
	},
}));

const getItemDetails = (data: OperationalConfigurationOverride): ValueDisplayProps[] => [
	{
		label: "Environment",
		node: data.environmentName,
	},
	{
		label: "Product instance",
		node: data.productInstanceName,
	},
	{
		label: "Component",
		node: data.componentName,
	},
	{
		label: "Component instance",
		node: data.componentInstanceName,
	},
	{
		label: "Value",
		node: data.value,
	},
	{
		label: "Created on",
		node: <DateDisplay date={data.createdDate} />,
	},
	{
		label: "Created by",
		node: data.createdBy,
	},
	{
		label: "Updated on",
		node: <DateDisplay date={data.lastUpdatedDate} />,
	},
	{
		label: "Updated by",
		node: data.lastUpdatedBy,
	},
];

/**
 * Display operational configuration overrides list
 * @param props
 */
export const OperationalConfigurationOverridesList = (props: OperationalConfigurationOverridesListProps) => {
	const classes = useStyles();
	const { enqueueSnackbar } = useSnackbar();

	const handleDelete = (operationalConfigurationOverrideId) => () => {
		if (!confirm("Are you sure you want to delete this operational configuration override?")) {
			return;
		}

		deleteOperationalConfigurationOverride(props.productId, operationalConfigurationOverrideId)()
			.then(() => {
				showSnack(enqueueSnackbar, "Operational configuration override deleted");
				props.onSuccess();
			})
			.catch((error) => {
				console.log({ error });
				showSnack(enqueueSnackbar, "There was an error while deleting the operational configuration override", "error");
			});
	};

	return (
		<div data-testid="operation-configuration-overrides-list">
			{props.operationalConfigurationOverrides && props.operationalConfigurationOverrides.length > 0 ? (
				<div>
					<List className={classes.list}>
						{props.operationalConfigurationOverrides.map((config) => (
							<ListItem
								key={config.operationalConfigurationOverrideId}
								className={classes.listItem}
								data-testid="operational-configuration-override-list-item"
							>
								<GridValueDisplayList
									displayItems={getItemDetails(config)}
									itemSpacing_md={4}
									itemSpacing_sm={6}
									itemSpacing_xs={12}
								></GridValueDisplayList>
								<PowFeature feature={POW_FEATURES.paConfigurations_Admin}>
									<div
										className={classes.actionButtons}
										data-testid="operational-configuration-override-action-buttons"
									>
										<Fab color="primary" size="small" aria-label="edit" onClick={() => props.handleEdit(config)}>
											<EditIcon />
										</Fab>
										<Fab
											color="secondary"
											size="small"
											aria-label="delete"
											style={{ marginLeft: 6 }}
											onClick={handleDelete(config.operationalConfigurationOverrideId)}
										>
											<DeleteIcon />
										</Fab>
									</div>
								</PowFeature>
							</ListItem>
						))}
					</List>
				</div>
			) : (
				<MessageCard message="No operational configuration overrides found." textAlign="left" />
			)}
		</div>
	);
};
